import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { PlusCircle, Edit2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const VehicleManagement = () => {
  const { token } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    veh_number: '',
    make: '',
    model: '',
    year: '',
    vin: '',
    tag: ''
  });

  // Fetch vehicles on component mount
  useEffect(() => {
    fetchVehicles();
  }, [token]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      toast.error('Failed to load vehicles');
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      veh_number: '',
      make: '',
      model: '',
      year: '',
      vin: '',
      tag: ''
    });
  };

  const handleAdd = () => {
    resetForm();
    setSelectedVehicle(null);
    setShowAddDialog(true);
  };

  const handleEdit = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      veh_number: vehicle.veh_number || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year?.toString() || '',
      vin: vehicle.vin || '',
      tag: vehicle.tag || ''
    });
    setShowEditDialog(true);
  };

  const submitAdd = async () => {
    try {
      const vehicleData = {
        ...formData,
        year: parseInt(formData.year)
      };

      await axios.post(`${API}/vehicles`, vehicleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Vehicle added successfully');
      setShowAddDialog(false);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Failed to add vehicle:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to add vehicle';
      toast.error(errorMessage);
    }
  };

  const submitEdit = async () => {
    if (!selectedVehicle) return;

    try {
      const vehicleData = {
        ...formData,
        year: formData.year ? parseInt(formData.year) : undefined
      };

      await axios.put(`${API}/vehicles/${selectedVehicle.id}`, vehicleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Vehicle updated successfully');
      setShowEditDialog(false);
      setSelectedVehicle(null);
      resetForm();
      fetchVehicles();
    } catch (error) {
      console.error('Failed to update vehicle:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update vehicle';
      toast.error(errorMessage);
    }
  };

  // hard-delete disabled by asset governance (retire via Admin Hub → Assets)

  // Filter vehicles based on search term
  const filteredVehicles = vehicles.filter(vehicle =>
    vehicle.veh_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vehicle.vin?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const VehicleForm = ({ title, onSubmit, onCancel }) => (
    <div className="space-y-4">
      <div>
        <Label htmlFor="veh_number">Vehicle Number *</Label>
        <Input
          id="veh_number"
          value={formData.veh_number}
          onChange={(e) => setFormData(prev => ({...prev, veh_number: e.target.value}))}
          placeholder="e.g., 09, 20, 24..."
          required
        />
      </div>

      <div>
        <Label htmlFor="make">Make *</Label>
        <Input
          id="make"
          value={formData.make}
          onChange={(e) => setFormData(prev => ({...prev, make: e.target.value}))}
          placeholder="e.g., FORD, CHEVY, GMC..."
          required
        />
      </div>

      <div>
        <Label htmlFor="model">Model *</Label>
        <Input
          id="model"
          value={formData.model}
          onChange={(e) => setFormData(prev => ({...prev, model: e.target.value}))}
          placeholder="e.g., F150, CITY EXPRESS..."
          required
        />
      </div>

      <div>
        <Label htmlFor="year">Year *</Label>
        <Input
          id="year"
          type="number"
          value={formData.year}
          onChange={(e) => setFormData(prev => ({...prev, year: e.target.value}))}
          placeholder="e.g., 2024"
          min="1900"
          max="2100"
          required
        />
      </div>

      <div>
        <Label htmlFor="vin">VIN</Label>
        <Input
          id="vin"
          value={formData.vin}
          onChange={(e) => setFormData(prev => ({...prev, vin: e.target.value}))}
          placeholder="17-digit VIN (optional)"
          maxLength="17"
        />
      </div>

      <div>
        <Label htmlFor="tag">Tag *</Label>
        <Input
          id="tag"
          value={formData.tag}
          onChange={(e) => setFormData(prev => ({...prev, tag: e.target.value}))}
          placeholder="License plate or internal tag"
          required
        />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit}>
          {title.includes('Add') ? 'Add Vehicle' : 'Update Vehicle'}
        </Button>
      </DialogFooter>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading vehicles...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl font-bold">Vehicle Management</CardTitle>
            <div className="flex gap-2">
              <Button onClick={fetchVehicles} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button onClick={handleAdd} className="flex items-center gap-2">
                <PlusCircle className="h-4 w-4" />
                Add Vehicle
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-6">
            <Input
              placeholder="Search by vehicle number, make, model, tag, or VIN..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          {/* Vehicle Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vehicle #</TableHead>
                  <TableHead>Make</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>VIN</TableHead>
                  <TableHead>Tag</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVehicles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan="7" className="text-center py-8">
                      {searchTerm ? 'No vehicles found matching your search.' : 'No vehicles found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVehicles.map((vehicle) => (
                    <TableRow key={vehicle.id}>
                      <TableCell className="font-medium">
                        <Badge variant="secondary">{vehicle.veh_number}</Badge>
                      </TableCell>
                      <TableCell>{vehicle.make}</TableCell>
                      <TableCell>{vehicle.model}</TableCell>
                      <TableCell>{vehicle.year}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {vehicle.vin || <span className="text-gray-400">—</span>}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{vehicle.tag}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(vehicle)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Vehicle count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredVehicles.length} of {vehicles.length} vehicles
          </div>
        </CardContent>
      </Card>

      {/* Add Vehicle Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Vehicle</DialogTitle>
          </DialogHeader>
          <VehicleForm
            title="Add Vehicle"
            onSubmit={submitAdd}
            onCancel={() => {
              setShowAddDialog(false);
              resetForm();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Vehicle Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Vehicle</DialogTitle>
          </DialogHeader>
          <VehicleForm
            title="Edit Vehicle"
            onSubmit={submitEdit}
            onCancel={() => {
              setShowEditDialog(false);
              setSelectedVehicle(null);
              resetForm();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Hard-delete removed — asset records are retained permanently (retire via Admin Hub → Assets). */}
      </div>
    </div>
  );
};

export default VehicleManagement;